<template>
  <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
  <div class="chat-application" style="height: inherit">
    <div
      class="body-content-overlay"
      :class="{'show': shallShowUserProfileSidebar ||  mqShallShowLeftSidebar}"
      @click="mqShallShowLeftSidebar=shallShowUserProfileSidebar=false"
    />

    <!-- Main Area -->
    <section class="chat-app-window">


      <!-- Start Chat Logo -->
      <div v-if="!activeSession" class="start-chat-area">
        <div v-if="loadingSession">
          <b-spinner variant="primary" type="grow" class="spinner-lg"/>
        </div>
        <div class="mb-1 start-chat-icon" v-if="!loadingSession">
          <feather-icon icon="MessageSquareIcon" size="56"/>
        </div>
        <h4 v-if="!loadingSession" class="sidebar-toggle start-chat-text" @click="startConversation">
          {{ $t('chat.startConversation') }}
        </h4>
      </div>

      <!-- Chat Content -->
      <div v-else class="active-chat ta-active-chat">
        <!-- Chat Navbar -->
        <div class="chat-navbar">
          <header class="chat-header">

            <!-- Avatar & Name -->
            <div class="d-flex align-items-center">

              <!-- Toggle Icon -->
              <div class="sidebar-toggle d-block d-lg-none mr-1">
                <feather-icon icon="MenuIcon" class="cursor-pointer" size="21" @click="mqShallShowLeftSidebar = true"/>
              </div>

              <div class="d-flex align-items-center cursor-pointer" @click="openClient">
                <b-avatar size="36" :src="activeSession.avatar" class="mr-1 badge-minimal"/>
                <div>
                  <h6 class="mb-0 text-capitalize">
                    {{ activeSession.sessionName || activeSession.clientName }}
                  </h6>
                  <small v-if="clientTyping" class="text-success">{{ $t('chat.typing') }}</small>
                </div>
              </div>
            </div>

            <!-- Contact Actions -->
            <div class="d-flex align-items-center">
            <feather-icon
                v-b-tooltip.hover
                :title="$t('chat.messages.finalizeSession')"
                icon="LogOutIcon"
                size="17"
                class="cursor-pointer d-sm-block d-none mr-1"
                @click="tryFinalizeSession"
              />
            </div>
          </header>
        </div>

        <!-- User Chat Area -->
        <vue-perfect-scrollbar ref="refChatLogPS" :settings="perfectScrollbarSettings" class="user-chats scroll-area">
          <chat-log v-bind:busy="loadingSession"
             v-bind:no-avatar="disableAvatar"
            :session="activeSession"
            :profile-user-avatar="profileUserDataMinimal.avatar"
             @loaded="scrollToBottomInChatLog"
          />
        </vue-perfect-scrollbar>

        <!-- Message Input -->
        <chat-input @message-sent="scrollToBottomSoon"/>
      </div>
    </section>


    <!-- Sidebar -->
    <portal to="content-renderer-sidebar-left">
      <chat-left-sidebar
          ref="chat-left-sidebar"
          :no-avatar="disableAvatar"
          :sessions="sessions"
          :contacts="contacts"
          :active-chat-contact-id="activeSession  ? activeSession.id : null"
          :shall-show-user-profile-sidebar.sync="shallShowUserProfileSidebar"
          :profile-user-data="profileUserData"
          :profile-user-minimal-data="profileUserDataMinimal"
          :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
          @show-user-profile="showUserProfileSidebar"
          @open-chat="openChatOfContact"
      />
    </portal>
  </div>
</template>

<script>
import store from '@/store'
import {
  BAvatar, BDropdown, BDropdownItem, BForm, BInputGroup, BFormInput, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { $themeBreakpoints } from '@themeConfig'
import ChatLeftSidebar from './ChatLeftSidebar.vue'
import ChatActiveChatContentDetailsSidedbar from './ChatActiveChatContentDetailsSidedbar.vue'
import ChatLog from './ChatLog.vue'
import useChat from './useChat'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ChatInput from '@/views/chat/ChatInput.vue'
import mixinBase from '@/components/mixin/mixinBase'

// Distance (px) from the bottom under which we still consider the agent "following" the chat
const STICK_TO_BOTTOM_THRESHOLD = 150
const TYPING_INDICATOR_MS = 4000

export default {
  mixins:[mixinBase],
  components: {
    ChatInput,
    /* BSV */ BAvatar, BDropdown, BDropdownItem, BForm, BInputGroup, BFormInput, BButton,/* 3rd Party */ VuePerfectScrollbar,/* SFC */ ChatLeftSidebar, ChatActiveChatContentDetailsSidedbar, ChatLog, },
  data: () => ({
    loadingSession: false, // Will be used to show loading spinner in start chat area
    disableAvatar: false, // Will be used to disable avatar badge
    // Shall Show User Profile Sidebar
    resolveAvatarBadgeVariant: useChat().resolveAvatarBadgeVariant,
    //Chats & Contacts
    contacts: [],
    //Single Chat
    perfectScrollbarSettings: { maxScrollbarLength: 150, },
    // User Profile Sidebar
    // ? Will contain all details of profile user (e.g. settings, about etc.)
    profileUserData: {},
    shallShowUserProfileSidebar: false,
    // UI + SM Devices
    // Left Sidebar Responsiveness
    mqShallShowLeftSidebar: false, //useResponsiveAppLeftSidebarVisibility().mqShallShowLeftSidebar,
    fetchChatAndContactsIntervalId: null,
    clientTyping: false,
    clientTypingTimeoutId: null,
  }),
  created() {
    // Fetch Chats & Contacts
    this.init()
    this.$root.$on('system::chat::messages::updated', this.scrollToBottomInChatLogOnNewMessage)
    this.$root.$on('system::notification::CHAT', this.onChatNotification)
    // Background refresh of the sessions list only — it must never reset the open chat
    this.fetchChatAndContactsIntervalId = setInterval(() => this.refreshSessions(), 60000);
  },
  beforeDestroy() {
    this.$root.$off('system::chat::messages::updated', this.scrollToBottomInChatLogOnNewMessage)
    this.$root.$off('system::notification::CHAT', this.onChatNotification)
    clearInterval(this.fetchChatAndContactsIntervalId);
    clearTimeout(this.clientTypingTimeoutId);
  },
  computed:{
    ...mapGetters("user", ["user"]),
    ...mapGetters("chat", ["sessions", "activeSession", "chatBySessionId", "typingBySessionId"]),
    profileUserDataMinimal() {
      let user = this.user || {}
      return {
        id: user.id,
        avatar: user.avatar || '1',
        fullName: user.fullName,
        status: user.status,
      }
    },
    activeTypingAt() {
      return this.activeSession ? this.typingBySessionId(this.activeSession.id) : null
    },
    routeChatQuery() {
      const query = this.$route?.query || {}
      return (query.session || '') + '|' + (query.sessionClientId || '')
    },
  },
  watch: {
    activeTypingAt(value) {
      clearTimeout(this.clientTypingTimeoutId)
      this.clientTyping = value != null && (Date.now() - value) < TYPING_INDICATOR_MS
      if (this.clientTyping) {
        this.clientTypingTimeoutId = setTimeout(() => { this.clientTyping = false }, TYPING_INDICATOR_MS)
      }
    },
    // Navigating to /clients/chat?sessionClientId=... while already on this page (e.g. from a client cell)
    routeChatQuery(value, oldValue) {
      if (value !== oldValue) {
        this.fetchChatAndContacts()
      }
    },
  },
  methods:{
    ...mapActions("chat", ["fetchChatSessions", "fetchChatSession", "sendChatMessage", 'initChatSession', "finalizeSession"]),
    ...mapMutations("chat", ["setActiveChat", "setSessionUnseen"]),
    init(){
      this.fetchChatAndContacts()
    },
    // ------------------------------------------------
    // Chats & Contacts
    // ------------------------------------------------
    onChatNotification(payload) {
      const params = payload?.params || {}
      if (params.type === 'new') {
        this.refreshSessions()
        return
      }
      if (params.type !== 'chatMessage' || params.sessionId == null) return
      if (this.sessions.find(s => s.id === params.sessionId) == null) {
        // Message for a session we don't have yet (new client chat)
        this.refreshSessions()
      } else if (this.activeSession == null || this.activeSession.id !== params.sessionId) {
        // Not the open chat — bump its unread badge and move it to the top of the list
        const session = this.sessions.find(s => s.id === params.sessionId)
        this.setSessionUnseen({ sessionId: params.sessionId, unseenMsgs: (session.unseenMsgs || 0) + 1 })
        if (params.message) {
          this.$store.commit('chat/setSessionLastMessage', params.message)
          this.$store.commit('chat/updateChat', [params.message])
        }
      }
    },
    getScrollEl() {
      const refChatLogPS = this.$refs.refChatLogPS
      if (!refChatLogPS) return null
      return refChatLogPS.$el || refChatLogPS
    },
    isNearBottom() {
      const el = this.getScrollEl()
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD
    },
    scrollToBottomInChatLogOnNewMessage(sessionId) {
      if(this.activeSession == null || sessionId !== this.activeSession.id) {
        return
      }
      let lastMessage = this.chatBySessionId(sessionId)?.messages?.slice(-1)[0]
      if(!lastMessage) {
        return
      }
      // Always follow my own messages; for incoming ones only if the agent is already at the
      // bottom — never yank the view away while they are reading older messages.
      const wasNearBottom = this.isNearBottom()
      if (lastMessage.senderId === this.user?.id || wasNearBottom) {
        this.scrollToBottomSoon()
      }
    },
    scrollToBottomSoon() {
      this.$nextTick(() => this.scrollToBottomInChatLog())
    },
    scrollToBottomInChatLog() {
      const scrollEl = this.getScrollEl()
      if(!scrollEl) {
        return
      }
      scrollEl.scrollTop = scrollEl.scrollHeight
      this.$refs.refChatLogPS?.update?.()
    },
    refreshSessions() {
      return this.fetchChatSessions()
          .then(() => this.$refs['chat-left-sidebar']?.setLoading(false))
          .catch(error => console.error('Error refreshing chat sessions:', error))
    },
    // Initial load / URL driven load — opens the chat referenced in the query params
    fetchChatAndContacts() {
      let $this = this
      const query = this.queryParams()
      const sessionParam = query.session != null && query.session !== '' ? query.session : null
      const clientParam = query.sessionClientId != null && query.sessionClientId !== '' ? query.sessionClientId : null
      const alreadyOpen = this.activeSession != null && (
          (sessionParam != null && this.activeSession.id === sessionParam) ||
          (sessionParam == null && clientParam != null && this.activeSession.clientId === clientParam))
      if ((sessionParam != null || clientParam != null) && !alreadyOpen) {
        $this.setActiveChat(null);
        $this.loadingSession = true;
      }
      return this.fetchChatSessions()
          .then(() => {
            if (alreadyOpen) {
              return
            }
            if (sessionParam != null) {
              return $this.openChatOfContact(sessionParam)
            } else if (clientParam != null) {
              return $this.openChatOfContactByClientId(clientParam)
            }
          })
          .catch(error => console.error('Error loading chat sessions:', error))
          .finally(() => {
            $this.loadingSession = false
            $this.$refs['chat-left-sidebar']?.setLoading(false)
          })
    },
    // ------------------------------------------------
    // Single Chat
    // ------------------------------------------------
    openChatOfContactByClientId(clientId) {
      let $this = this
      let session = this.sessions.find(s => s.clientId === clientId)
      if (session != null) {
        return this.openChatOfContact(session.id)
      }
      this.loadingSession = true
      return this.initChatSession(clientId)
          .then(session => {
            $this.pushState({ session: session.id, sessionClientId: session.clientId })
            $this.scrollToBottomSoon()
          })
          .catch(error => console.error('Error starting chat session:', error))
          .finally(() => $this.loadingSession = false)
    },
    openChatOfContact(sessionId) {
      let $this = this
      if (sessionId == null) return Promise.resolve()
      const switching = this.activeSession == null || this.activeSession.id !== sessionId
      this.loadingSession = switching && this.sessions.find(s => s.id === sessionId) == null
      if (switching) {
        this.setActiveChat(sessionId);
      }
      // if SM device =>  Close Chat & Contacts left sidebar
      this.mqShallShowLeftSidebar = false
      return this.fetchChatSession(sessionId).then(session => {
        // The agent may have clicked another chat meanwhile
        if ($this.activeSession == null || $this.activeSession.id === sessionId) {
          $this.setActiveChat(sessionId);
        }
        $this.pushState({
          session: sessionId,
          sessionClientId: session?.clientId || null
        })
        if (switching) {
          $this.scrollToBottomSoon()
        }
      }).catch(error => {
        console.error('Error opening chat session:', error)
        // Session no longer available (closed / no access) — clear the stale URL
        if ($this.activeSession == null || $this.activeSession.id === sessionId) {
          $this.setActiveChat(null)
          $this.pushState({ session: null, sessionClientId: null })
        }
      }).finally(() => {
        $this.loadingSession = false
      })
    },

    showUserProfileSidebar() {
      //TODO: Uncomment for get and show profile user data sidebar
    },
    startConversation() {
      if (store.state.app.windowWidth < $themeBreakpoints.lg) {
        this.mqShallShowLeftSidebar = true
      }
    },

    openClient() {
      if (this.activeSession == null || this.activeSession.clientId == null) {
        return
      }
      this.showClient(this.activeSession.clientId)
    },
    tryFinalizeSession(){
      let $this = this
      const clientId = this.activeSession?.clientId
      if (clientId == null) return
      this.$bvModal.msgBoxConfirm(this.$t('chat.messages.finalizeSessionQuestion'), {
        title: this.$t('chat.messages.finalizeSession'),
        size: 'sm', buttonSize: 'sm', okVariant: 'danger',
        okTitle: this.$t('operations.yes'), cancelTitle: this.$t('operations.no'),
        hideHeaderClose: false, centered: true
      }).then(value => {
            if(!value) return
            return $this.finalizeSession(clientId).then(() => {
              $this.pushState({ session: null, sessionClientId: null });
              return $this.refreshSessions()
            })
          })
          .catch(error => console.error('Error finalizing chat session:', error))
    }
  },

}
</script>

<style lang="scss">
// Flex column so the message input can grow (multi-line) without covering the messages
.chat-app-window .active-chat.ta-active-chat {
  display: flex;
  flex-direction: column;

  .chat-navbar {
    flex: 0 0 auto;
  }
  .user-chats {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
  }
  .chat-app-form {
    flex: 0 0 auto;
  }
}
</style>
